import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "tailwindcss/tailwind.css";
import Select from "react-select";
import {
  DocumentIcon,
  ArrowDownTrayIcon,
  PlusIcon,
  ArrowLeftIcon,
  ArrowPathIcon,
  TrashIcon,
  MagnifyingGlassIcon,
} from "@heroicons/react/24/outline";
import axios from "axios";
import { BASE_URL } from "../../config";

const PROVIDER_DROPDOWN_LIMIT = 100;
const tabSectionClass = "space-y-4";
const fieldGridClass = "grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4";
const wideFieldClass = "md:col-span-2 xl:col-span-3";
const normalizeOptionText = (value) => String(value ?? "").trim();
const isValidEmail = (value) => {
  const email = String(value ?? "").trim();
  if (!email) return true;
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
};

const isPhicDateRangeValid = (dateFrom, dateTo) => {
  if (!dateFrom || !dateTo) return true;
  return new Date(dateFrom) <= new Date(dateTo);
};

const buildUniqueOptions = (values = []) => {
  const optionsByKey = new Map();

  values.forEach(value => {
    const text = normalizeOptionText(value);
    if (!text) return;

    const key = text.toLowerCase();
    if (!optionsByKey.has(key)) {
      optionsByKey.set(key, { value: text, label: text });
    }
  });

  return Array.from(optionsByKey.values()).sort((a, b) =>
    a.label.localeCompare(b.label)
  );
};

const formatProviderOptions = (providerList = []) =>
  providerList
    .filter(provider => provider?.provider_code)
    .map(provider => ({
      value: provider.provider_code,
      label: `${provider.provider_name || provider.provider_code} (${provider.provider_code})`,
      name: provider.provider_name || "",
      tin: provider.tin || "",
    }));

const mergeProviderOptions = (currentOptions = [], nextOptions = []) => {
  const providersByCode = new Map();
  currentOptions.forEach(provider => providersByCode.set(provider.value, provider));
  nextOptions.forEach(provider => providersByCode.set(provider.value, provider));

  return Array.from(providersByCode.values()).sort((a, b) =>
    String(a.name || a.label).localeCompare(String(b.name || b.label))
  );
};

const PhysicianEnrollmentForm = () => {
  const { physicianId } = useParams();
  const navigate = useNavigate();
  const isEditMode = !!physicianId;
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingHomeAddress, setIsLoadingHomeAddress] = useState({
    region: false,
    province: false,
    towncity: false,
    barangay: false,
  });
  const [isLoadingProvincialAddress, setIsLoadingProvincialAddress] = useState({
    region: false,
    province: false,
    towncity: false,
    barangay: false,
  });
  const [formData, setFormData] = useState({
    last_name: "",
    first_name: "",
    middle_name: "",
    extension: "",
    mothers_name: "",
    nationality: "",
    date_of_birth: "",
    age: "",
    civil_status: "Single",
    gender: "",
    specialization: "",
    sub_specialization: "",
    ip_prescription_period: "0",
    op_prescription_period: "",
    status_flag: "1",
    remarks: "",
    pcp_spec_tag: "0",
    pcp_rate: "",
    spec_rate: "",
    tel_no: "",
    mobile_no: "",
    email_add: "",
    bank_acc_name: "",
    bank_acct_no: "",
    bank_id: "",
    bank_branch: "",
    fraud_flag: false,
    pef_upload_id: "",
    pef_file_name: "",
    pef_signed_url: "",
    pef_expires_at: "",
    bank_proof_upload_id: "",
    bank_proof_file_name: "",
    bank_proof_signed_url: "",
    bank_proof_expires_at: "",
    tin_no: "",
    sss_no: "",
    philhealth_no: "",
    phic_no: "",
    phic_from: "",
    phic_to: "",
    prc_no: "",
    prc_expiry: "",
    tax_percentage: "10.00",
    vatable: "0",
    sworn_attachment_upload_id: "",
    sworn_attachment_file_name: "",
    sworn_attachment_signed_url: "",
    sworn_attachment_expires_at: "",
    conforme_file_upload_id: "",
    conforme_file_file_name: "",
    conforme_file_signed_url: "",
    conforme_file_expires_at: "",
    home_streetno_bldg: "",
    home_region: "",
    home_province: "",
    home_towncity: "",
    home_barangay: "",
    home_postalcode: "",
    provincial_streetno_bldg: "",
    provincial_region: "",
    provincial_province: "",
    provincial_towncity: "",
    provincial_barangay: "",
    provincial_postalcode: "",
    maintenance_update_for: "",
    maintenance_effectivity_date: "",
    maintenance_reason: "",
    maintenance_remarks: "",
    maintenance_lastupdate_date: "",
    maintenance_update_by: "",
    provider_affiliations: [],
  });
  const [providerForm, setProviderForm] = useState({
    provider_code: "",
    provider_name: "",
    room_no: "",
    schedule: "",
    remarks: "",
  });
  const [homeRegions, setHomeRegions] = useState([]);
  const [homeProvinces, setHomeProvinces] = useState([]);
  const [homeTownCities, setHomeTownCities] = useState([]);
  const [homeBarangays, setHomeBarangays] = useState([]);
  const [provincialRegions, setProvincialRegions] = useState([]);
  const [provincialProvinces, setProvincialProvinces] = useState([]);
  const [provincialTownCities, setProvincialTownCities] = useState([]);
  const [provincialBarangays, setProvincialBarangays] = useState([]);
  const [banks, setBanks] = useState([]);
  const [initialBankDetails, setInitialBankDetails] = useState(null);
  const [providers, setProviders] = useState([]);
  const [providerSearch, setProviderSearch] = useState("");
  const [isLoadingProviders, setIsLoadingProviders] = useState(false);
  const [nationalities, setNationalities] = useState([]);
  const [specializations, setSpecializations] = useState([]);
  const [subSpecializations, setSubSpecializations] = useState([]);
  const [specializationRows, setSpecializationRows] = useState([]);
  const [errors, setErrors] = useState({});
  const [activeTab, setActiveTab] = useState("information");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [searchQuery, setSearchQuery] = useState("");

  const getAuthHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem("authentication/token")}`,
  });

  // Fetch physician details for edit mode
  useEffect(() => {
    if (isEditMode) {
      const fetchPhysician = async () => {
        setIsLoading(true);
        try {
          const response = await axios.get(`${BASE_URL}/physicians/${physicianId}`, {
            headers: getAuthHeaders(),
          });
          const physician = response.data.data || {};
          setInitialBankDetails({
            bank_acc_name: physician.bank_acc_name || "",
            bank_acct_no: physician.bank_acct_no || "",
            bank_id: physician.bank_id || "",
            bank_branch: physician.bank_branch || "",
          });
          setFormData(prev => ({
            ...prev,
            ...physician,
            vatable: physician.vatable === null || physician.vatable === undefined ? "0" : String(physician.vatable),
          }));

          try {
            const maintenanceResponse = await axios.get(
              `${BASE_URL}/physicians/${physicianId}/maintenance`,
              { headers: getAuthHeaders() }
            );
            const maintenance = maintenanceResponse.data?.data?.maintenance || {};
            setFormData(prev => ({
              ...prev,
              maintenance_update_for: maintenance.update_for || "",
              maintenance_effectivity_date: maintenance.effectivity_date || "",
              maintenance_reason: maintenance.reason || "",
              maintenance_remarks: maintenance.remarks || "",
              maintenance_lastupdate_date: maintenance.lastupdate_date || "",
              maintenance_update_by: maintenance.update_by || "",
            }));
          } catch (maintenanceError) {
            console.error("Failed to fetch physician maintenance:", maintenanceError);
          }

          if (response.data.data.home_region) {
            fetchHomeProvinces(response.data.data.home_region);
          }
          if (response.data.data.home_province) {
            fetchHomeTownCities(response.data.data.home_province);
          }
          if (response.data.data.home_towncity) {
            fetchHomeBarangays(response.data.data.home_towncity);
          }
          if (response.data.data.provincial_region) {
            fetchProvincialProvinces(response.data.data.provincial_region);
          }
          if (response.data.data.provincial_province) {
            fetchProvincialTownCities(response.data.data.provincial_province);
          }
          if (response.data.data.provincial_towncity) {
            fetchProvincialBarangays(response.data.data.provincial_towncity);
          }
        } catch (error) {
          toast.error("Failed to fetch physician details: " + (error.response?.data?.message || error.message));
          setTimeout(() => navigate("/physician"), 2000);
        } finally {
          setIsLoading(false);
        }
      };
      fetchPhysician();
    }
  }, [isEditMode, physicianId, navigate]);

  // Fetch banks
  useEffect(() => {
    const fetchBanks = async () => {
      try {
        setIsLoading(true);
        const response = await axios.get(`${BASE_URL}/bank/get-banks`, {
          headers: getAuthHeaders(),
        });
        setBanks(response.data.data.banks.map(bank => ({ value: bank.bank_id, label: bank.bank_name })));
      } catch (error) {
        console.error("Error fetching banks:", error);
        toast.error("Failed to load banks: " + (error.response?.data?.message || error.message));
      } finally {
        setIsLoading(false);
      }
    };
    fetchBanks();
  }, []);

  // Fetch regions
  useEffect(() => {
    const fetchRegions = async () => {
      try {
        setIsLoading(true);
        setIsLoadingHomeAddress(prev => ({ ...prev, region: true }));
        setIsLoadingProvincialAddress(prev => ({ ...prev, region: true }));
        const response = await axios.get(`${BASE_URL}/address/get-ref-address`, {
          headers: getAuthHeaders(),
        });
        const regions = response.data.data.map(region => ({ value: region.region_code, label: region.region }));
        setHomeRegions(regions);
        setProvincialRegions(regions);
      } catch (error) {
        console.error("Error fetching regions:", error);
        toast.error("Failed to load regions: " + (error.response?.data?.message || error.message));
      } finally {
        setIsLoading(false);
        setIsLoadingHomeAddress(prev => ({ ...prev, region: false }));
        setIsLoadingProvincialAddress(prev => ({ ...prev, region: false }));
      }
    };
    fetchRegions();
  }, []);

  // Fetch nationalities
  useEffect(() => {
    const fetchNationalities = async () => {
      try {
        setIsLoading(true);
        const response = await axios.get(`${BASE_URL}/physicians/nationalities`, {
          headers: getAuthHeaders(),
        });
        setNationalities(response.data.data.map(n => ({ value: n.nationality, label: n.nationality })));
      } catch (error) {
        console.error("Error fetching nationalities:", error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchNationalities();
  }, []);

  // Fetch specializations
  useEffect(() => {
    const fetchSpecializations = async () => {
      try {
        setIsLoading(true);
        const response = await axios.get(`${BASE_URL}/physicians/specializations`, {
          headers: getAuthHeaders(),
        });
        const specs = response.data.data
          .filter(item => item.status_flag === 1)
          .map(item => ({
            specialization: normalizeOptionText(item.specialization),
            sub_specialization: normalizeOptionText(item.sub_specialization),
          }))
          .filter(item => item.specialization);

        setSpecializationRows(specs);
        setSpecializations(buildUniqueOptions(specs.map(s => s.specialization)));
        setSubSpecializations(buildUniqueOptions(specs.map(s => s.sub_specialization)));
      } catch (error) {
        console.error("Error fetching specializations:", error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchSpecializations();
  }, []);

  const fetchProviders = async (search = "") => {
    try {
      setIsLoadingProviders(true);
      const response = await axios.get(`${BASE_URL}/providers/list/for-dropdown-select`, {
        headers: getAuthHeaders(),
        params: {
          search: search.trim(),
          limit: PROVIDER_DROPDOWN_LIMIT,
        },
      });
      const nextProviders = formatProviderOptions(response.data?.data?.providers || []);
      setProviders(prev => mergeProviderOptions(prev, nextProviders));
    } catch (error) {
      console.error("Error fetching providers:", error);
      toast.error("Failed to load providers: " + (error.response?.data?.message || error.message));
    } finally {
      setIsLoadingProviders(false);
    }
  };

  // Fetch providers and search server-side as the user types.
  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      fetchProviders(providerSearch);
    }, providerSearch.trim() ? 350 : 0);

    return () => clearTimeout(debounceTimer);
  }, [providerSearch]);

  // Address handlers
  const fetchHomeProvinces = async (regionCode) => {
    if (!regionCode) return;
    try {
      setIsLoadingHomeAddress(prev => ({ ...prev, province: true }));
      const resp = await axios.get(`${BASE_URL}/address/get-ref-address`, {
        headers: getAuthHeaders(),
        params: { region_code: regionCode },
      });
      setHomeProvinces(resp.data.data.map(p => ({ value: p.province_code, label: p.province })));
    } catch (err) {
      console.error("Error fetching home provinces:", err);
      toast.error("Failed to load home provinces.");
    } finally {
      setIsLoadingHomeAddress(prev => ({ ...prev, province: false }));
    }
  };

  const fetchHomeTownCities = async (provinceCode) => {
    if (!provinceCode) return;
    try {
      setIsLoadingHomeAddress(prev => ({ ...prev, towncity: true }));
      const resp = await axios.get(`${BASE_URL}/address/get-ref-address`, {
        headers: getAuthHeaders(),
        params: { province_code: provinceCode },
      });
      setHomeTownCities(resp.data.data.map(c => ({ value: c.city_code, label: c.city })));
    } catch (err) {
      console.error("Error fetching home town/cities:", err);
      toast.error("Failed to load home town/cities.");
    } finally {
      setIsLoadingHomeAddress(prev => ({ ...prev, towncity: false }));
    }
  };

  const fetchHomeBarangays = async (cityCode) => {
    if (!cityCode) return;
    try {
      setIsLoadingHomeAddress(prev => ({ ...prev, barangay: true }));
      const resp = await axios.get(`${BASE_URL}/address/get-ref-address`, {
        headers: getAuthHeaders(),
        params: { city_code: cityCode },
      });
      setHomeBarangays(resp.data.data.map(b => ({ value: b.baranggay_code, label: b.baranggay, postalcode: b.postcode })));
    } catch (err) {
      console.error("Error fetching home barangays:", err);
      toast.error("Failed to load home barangays.");
    } finally {
      setIsLoadingHomeAddress(prev => ({ ...prev, barangay: false }));
    }
  };

  const fetchProvincialProvinces = async (regionCode) => {
    if (!regionCode) return;
    try {
      setIsLoadingProvincialAddress(prev => ({ ...prev, province: true }));
      const resp = await axios.get(`${BASE_URL}/address/get-ref-address`, {
        headers: getAuthHeaders(),
        params: { region_code: regionCode },
      });
      setProvincialProvinces(resp.data.data.map(p => ({ value: p.province_code, label: p.province })));
    } catch (err) {
      console.error("Error fetching provincial provinces:", err);
      toast.error("Failed to load provincial provinces.");
    } finally {
      setIsLoadingProvincialAddress(prev => ({ ...prev, province: false }));
    }
  };

  const fetchProvincialTownCities = async (provinceCode) => {
    if (!provinceCode) return;
    try {
      setIsLoadingProvincialAddress(prev => ({ ...prev, towncity: true }));
      const resp = await axios.get(`${BASE_URL}/address/get-ref-address`, {
        headers: getAuthHeaders(),
        params: { province_code: provinceCode },
      });
      setProvincialTownCities(resp.data.data.map(c => ({ value: c.city_code, label: c.city })));
    } catch (err) {
      console.error("Error fetching provincial town/cities:", err);
      toast.error("Failed to load provincial town/cities.");
    } finally {
      setIsLoadingProvincialAddress(prev => ({ ...prev, towncity: false }));
    }
  };

  const fetchProvincialBarangays = async (cityCode) => {
    if (!cityCode) return;
    try {
      setIsLoadingProvincialAddress(prev => ({ ...prev, barangay: true }));
      const resp = await axios.get(`${BASE_URL}/address/get-ref-address`, {
        headers: getAuthHeaders(),
        params: { city_code: cityCode },
      });
      setProvincialBarangays(resp.data.data.map(b => ({ value: b.baranggay_code, label: b.baranggay, postalcode: b.postcode })));
    } catch (err) {
      console.error("Error fetching provincial barangays:", err);
      toast.error("Failed to load provincial barangays.");
    } finally {
      setIsLoadingProvincialAddress(prev => ({ ...prev, barangay: false }));
    }
  };

  const handleHomeRegionChange = async (selected) => {
    const value = selected ? selected.value : "";
    setFormData(prev => ({
      ...prev,
      home_region: value,
      home_province: "",
      home_towncity: "",
      home_barangay: "",
      home_postalcode: "",
    }));
    setHomeProvinces([]);
    setHomeTownCities([]);
    setHomeBarangays([]);
    if (!value) return;
    await fetchHomeProvinces(value);
  };

  const handleHomeProvinceChange = async (selected) => {
    const value = selected ? selected.value : "";
    setFormData(prev => ({
      ...prev,
      home_province: value,
      home_towncity: "",
      home_barangay: "",
      home_postalcode: "",
    }));
    setHomeTownCities([]);
    setHomeBarangays([]);
    if (!value) return;
    await fetchHomeTownCities(value);
  };

  const handleHomeTownCityChange = async (selected) => {
    const value = selected ? selected.value : "";
    setFormData(prev => ({
      ...prev,
      home_towncity: value,
      home_barangay: "",
      home_postalcode: "",
    }));
    setHomeBarangays([]);
    if (!value) return;
    await fetchHomeBarangays(value);
  };

  const handleHomeBarangayChange = (selected) => {
    const value = selected ? selected.value : "";
    const postal = selected ? selected.postalcode || "" : "";
    setFormData(prev => ({
      ...prev,
      home_barangay: value,
      home_postalcode: postal,
    }));
  };

  const handleProvincialRegionChange = async (selected) => {
    const value = selected ? selected.value : "";
    setFormData(prev => ({
      ...prev,
      provincial_region: value,
      provincial_province: "",
      provincial_towncity: "",
      provincial_barangay: "",
      provincial_postalcode: "",
    }));
    setProvincialProvinces([]);
    setProvincialTownCities([]);
    setProvincialBarangays([]);
    if (!value) return;
    await fetchProvincialProvinces(value);
  };

  const handleProvincialProvinceChange = async (selected) => {
    const value = selected ? selected.value : "";
    setFormData(prev => ({
      ...prev,
      provincial_province: value,
      provincial_towncity: "",
      provincial_barangay: "",
      provincial_postalcode: "",
    }));
    setProvincialTownCities([]);
    setProvincialBarangays([]);
    if (!value) return;
    await fetchProvincialTownCities(value);
  };

  const handleProvincialTownCityChange = async (selected) => {
    const value = selected ? selected.value : "";
    setFormData(prev => ({
      ...prev,
      provincial_towncity: value,
      provincial_barangay: "",
      provincial_postalcode: "",
    }));
    setProvincialBarangays([]);
    if (!value) return;
    await fetchProvincialBarangays(value);
  };

  const handleProvincialBarangayChange = (selected) => {
    const value = selected ? selected.value : "";
    const postal = selected ? selected.postalcode || "" : "";
    setFormData(prev => ({
      ...prev,
      provincial_barangay: value,
      provincial_postalcode: postal,
    }));
  };

  const handleInputChange = (e, field) => {
    const value = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setFormData(prev => ({ ...prev, [field]: value }));
    setErrors(prev => ({ ...prev, [field]: "" }));
  };

  const handleSelectChange = (selected, field) => {
    const value = selected ? selected.value : "";
    setFormData(prev => ({
      ...prev,
      [field]: value,
      ...(field === "specialization" ? { sub_specialization: "" } : {}),
    }));
    setErrors(prev => ({
      ...prev,
      [field]: "",
      ...(field === "specialization" ? { sub_specialization: "" } : {}),
    }));
  };

  const handleProviderFormChange = (e) => {
    const { name, value } = e.target;
    setProviderForm(prev => ({ ...prev, [name]: value }));
    setErrors(prev => ({ ...prev, [name]: "" }));
  };

  const handleProviderSelect = (selected) => {
    const value = selected ? selected.value : "";
    setProviderForm(prev => ({
      ...prev,
      provider_code: value,
      provider_name: selected?.name || "",
    }));
    setErrors(prev => ({ ...prev, provider_code: "" }));
  };

  const normalizeBankText = (value) => String(value ?? "").trim();
  const normalizeBankAccountNo = (value) => normalizeBankText(value).replace(/[\s-]/g, "");
  const normalizeBankId = (value) => normalizeBankText(value);

  const physicianBankChanged = () => {
    if (!isEditMode || !initialBankDetails) return false;
    return (
      normalizeBankText(initialBankDetails.bank_acc_name) !== normalizeBankText(formData.bank_acc_name) ||
      normalizeBankAccountNo(initialBankDetails.bank_acct_no) !== normalizeBankAccountNo(formData.bank_acct_no) ||
      normalizeBankText(initialBankDetails.bank_branch) !== normalizeBankText(formData.bank_branch) ||
      normalizeBankId(initialBankDetails.bank_id) !== normalizeBankId(formData.bank_id)
    );
  };

  const validateProviderForm = () => {
    const newErrors = {};
    if (!providerForm.provider_code) {
      newErrors.provider_code = "Provider is required.";
    }
    return newErrors;
  };

  const addProviderAffiliation = () => {
    const providerErrors = validateProviderForm();
    if (Object.keys(providerErrors).length > 0) {
      setErrors(providerErrors);
      return;
    }
    if (formData.provider_affiliations.some(aff => aff.provider_code === providerForm.provider_code)) {
      setErrors(prev => ({
        ...prev,
        provider_code: "This provider has already been added.",
      }));
      return;
    }
    setFormData(prev => ({
      ...prev,
      provider_affiliations: [
        ...prev.provider_affiliations,
        {
          provider_code: providerForm.provider_code,
          provider_name: providerForm.provider_name,
          room_no: providerForm.room_no,
          schedule: providerForm.schedule,
          remarks: providerForm.remarks,
        },
      ],
    }));
    setProviderForm({ provider_code: "", provider_name: "", room_no: "", schedule: "", remarks: "" });
    setErrors(prev => ({ ...prev, provider_code: "", room_no: "", schedule: "", remarks: "" }));
    setCurrentPage(1);
  };

  const removeProviderAffiliation = (index) => {
    setFormData(prev => ({
      ...prev,
      provider_affiliations: prev.provider_affiliations.filter((_, i) => i !== index),
    }));
  };

  const handleFileUpload = async (e, field) => {
    const file = e.target.files[0];
    if (!file) {
      toast.error("No file selected.");
      return;
    }
    const allowedTypes = [
      "image/png", "image/jpeg", "image/gif", "image/bmp",
      "application/pdf", "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      "application/vnd.ms-excel",
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      "text/csv",
    ];
    if (!allowedTypes.includes(file.type)) {
      toast.error("Invalid file type.");
      return;
    }
    const fd = new FormData();
    fd.append("file", file);
    fd.append("uploader_type", field);
    try {
      setIsLoading(true);
      const resp = await axios.post(`${BASE_URL}/physicians/document/upload`, fd, {
        headers: {
          ...getAuthHeaders(),
          "Content-Type": "multipart/form-data",
        },
      });
      const data = resp.data.data;
      setFormData(prev => ({
        ...prev,
        [`${field}_upload_id`]: data.upload_id || "",
        [`${field}_file_name`]: data.original_file_name || "",
        [`${field}_signed_url`]: data.signed_url || "",
        [`${field}_expires_at`]: data.expires_at || "",
      }));
      toast.success(`${field.replace("_", " ")} uploaded successfully.`);
    } catch (err) {
      console.error("Error uploading file:", err);
      toast.error("Failed to upload file.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleBankProofUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) {
      toast.error("No file selected.");
      return;
    }

    const fd = new FormData();
    fd.append("file", file);
    fd.append("entity_type", "physician");
    if (physicianId) {
      fd.append("entity_id", physicianId);
    }

    try {
      setIsLoading(true);
      const resp = await axios.post(`${BASE_URL}/physicians/bank-proof/upload`, fd, {
        headers: {
          ...getAuthHeaders(),
          "Content-Type": "multipart/form-data",
        },
      });
      const data = resp.data.data || {};
      setFormData(prev => ({
        ...prev,
        bank_proof_upload_id: data.upload_id || "",
        bank_proof_file_name: data.original_file_name || "",
        bank_proof_signed_url: data.signed_url || "",
        bank_proof_expires_at: data.expires_at || "",
      }));
      toast.success("Bank proof uploaded successfully.");
    } catch (err) {
      console.error("Error uploading bank proof:", err);
      toast.error(err.response?.data?.message || "Failed to upload bank proof.");
    } finally {
      setIsLoading(false);
    }
  };

  const regenerateUrl = async (field) => {
    console.log(formData)
    const uploadId = formData[`${field}_upload_id`];
    if (!uploadId) {
      toast.error("No upload id to regenerate.");
      return;
    }
    try {
      setIsLoading(true);
      const resp = await axios.post(`${BASE_URL}/physicians/document/regenerate-url`, { upload_id: uploadId }, {
        headers: getAuthHeaders(),
      });
      setFormData(prev => ({
        ...prev,
        [`${field}_signed_url`]: resp.data.data.signed_url || "",
        [`${field}_expires_at`]: resp.data.data.expires_at || "",
      }));
      toast.success("URL regenerated successfully.");
    } catch (err) {
      console.error("Error regenerating URL:", err);
      toast.error("Failed to regenerate URL.");
    } finally {
      setIsLoading(false);
    }
  };

  const isFileUrlExpired = (expiresAt) => {
    if (!expiresAt) return false;
    return new Date(expiresAt) < new Date();
  };

  const selectedSpecialization = normalizeOptionText(formData.specialization);
  const selectedSubSpecialization = normalizeOptionText(formData.sub_specialization);
  const selectedSpecializationKey = selectedSpecialization.toLowerCase();
  const filteredSubSpecializationOptions = selectedSpecialization
    ? buildUniqueOptions(
        specializationRows
          .filter(row => row.specialization.toLowerCase() === selectedSpecializationKey)
          .map(row => row.sub_specialization)
      )
    : subSpecializations;
  const selectedSpecializationOption =
    specializations.find(
      option => normalizeOptionText(option.value).toLowerCase() === selectedSpecializationKey
    ) ||
    (selectedSpecialization
      ? { value: selectedSpecialization, label: selectedSpecialization }
      : null);
  const selectedSubSpecializationOption =
    filteredSubSpecializationOptions.find(
      option =>
        normalizeOptionText(option.value).toLowerCase() ===
        selectedSubSpecialization.toLowerCase()
    ) ||
    (selectedSubSpecialization
      ? { value: selectedSubSpecialization, label: selectedSubSpecialization }
      : null);

  // const filteredAffiliations = formData.provider_affiliations.filter(aff => {
  //   const provider = providers.find(p => p.value === aff.provider_code);
  //   // const providerName = providers.find(p => p.value === aff.provider_name);
  //   // const providerName = provider ? provider.label.toLowerCase() : "";
  //   // return providerName.includes(searchQuery.toLowerCase()) || aff.provider_code.toLowerCase().includes(searchQuery.toLowerCase());
  //   return aff.provider_name.toLowerCase().includes(searchQuery.toLowerCase()) || aff.provider_code.toLowerCase().includes(searchQuery.toLowerCase());
  // });

  const q = (searchQuery ?? "").toLowerCase(); 
  const filteredAffiliations = formData.provider_affiliations.filter(aff => {
    const provider = (aff.provider_code ?? "").toLowerCase();
    const providerName = (aff.provider_name ?? "").toLowerCase();
    return provider.includes(q) || providerName.includes(q);
  }); 

  const totalItems = filteredAffiliations.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const paginatedAffiliations = filteredAffiliations.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const handleItemsPerPageChange = (e) => {
    setItemsPerPage(Number(e.target.value));
    setCurrentPage(1);
  };

  const getFieldErrorMessage = (fieldKey) => {
    const fieldError = errors?.[fieldKey];
    if (Array.isArray(fieldError)) {
      return fieldError[0] || "";
    }
    return typeof fieldError === "string" ? fieldError : "";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrors({});

    if (activeTab === "maintenance") {
      if (!isEditMode) {
        toast.error("Maintenance update is only available in edit mode.");
        return;
      }

      const maintenanceErrors = {};
      const updateFor = String(formData.maintenance_update_for || "").trim();
      const effectivityDate = String(formData.maintenance_effectivity_date || "").trim();
      const reason = String(formData.maintenance_reason || "").trim();
      const remarks = String(formData.maintenance_remarks || "").trim();

      if (!updateFor) {
        maintenanceErrors.maintenance_update_for = ["Update For is required."];
      }
      if (!effectivityDate) {
        maintenanceErrors.maintenance_effectivity_date = ["Effectivity Date is required."];
      }
      if (!reason) {
        maintenanceErrors.maintenance_reason = ["Reason is required."];
      }

      if (Object.keys(maintenanceErrors).length > 0) {
        setErrors(maintenanceErrors);
        toast.error("Please fill in all required maintenance fields.");
        return;
      }

      try {
        setIsLoading(true);
        const response = await axios.post(
          `${BASE_URL}/physicians/${physicianId}/maintenance`,
          {
            update_for: updateFor,
            effectivity_date: effectivityDate,
            reason,
            remarks,
            expiry_date: "",
          },
          { headers: getAuthHeaders() }
        );

        const maintenance = response.data?.data?.maintenance || {};
        setFormData(prev => ({
          ...prev,
          maintenance_update_for: maintenance.update_for ?? prev.maintenance_update_for,
          maintenance_effectivity_date: maintenance.effectivity_date ?? prev.maintenance_effectivity_date,
          maintenance_reason: maintenance.reason ?? prev.maintenance_reason,
          maintenance_remarks: maintenance.remarks ?? prev.maintenance_remarks,
          maintenance_lastupdate_date: maintenance.lastupdate_date ?? prev.maintenance_lastupdate_date,
          maintenance_update_by: maintenance.update_by ?? prev.maintenance_update_by,
        }));
        setErrors({});
        toast.success(response.data?.message || "Physician maintenance updated successfully.");
      } catch (err) {
        const apiErrors = err?.response?.data?.errors || {};
        const mappedErrors = {};

        if (apiErrors.update_for?.length) {
          mappedErrors.maintenance_update_for = apiErrors.update_for;
        }
        if (apiErrors.effectivity_date?.length) {
          mappedErrors.maintenance_effectivity_date = apiErrors.effectivity_date;
        }
        if (apiErrors.reason?.length) {
          mappedErrors.maintenance_reason = apiErrors.reason;
        }
        if (apiErrors.remarks?.length) {
          mappedErrors.maintenance_remarks = apiErrors.remarks;
        }

        if (Object.keys(mappedErrors).length > 0) {
          setErrors(mappedErrors);
        }

        const maintenanceErrorMessage =
          err?.response?.data?.message || "Failed to update physician maintenance.";
        toast.error(maintenanceErrorMessage);
        console.error("Error submitting maintenance:", maintenanceErrorMessage);
      } finally {
        setIsLoading(false);
      }

      return;
    }

    const formValidationErrors = {};
    const emailValue = String(formData.email_add || "").trim();

    if (!isValidEmail(emailValue)) {
      formValidationErrors.email_add = ["Please enter a valid email address."];
    }

    if (!isPhicDateRangeValid(formData.phic_from, formData.phic_to)) {
      formValidationErrors.phic_to = [
        "PHIC Date To must be on or after PHIC Date From.",
      ];
    }

    if (Object.keys(formValidationErrors).length > 0) {
      setErrors(formValidationErrors);
      setActiveTab(formValidationErrors.email_add ? "contact" : "license");
      toast.error("Please correct the highlighted validation errors.");
      return;
    }

    if (physicianBankChanged() && !formData.bank_proof_upload_id) {
      setActiveTab("bank");
      toast.error("Bank proof is required when physician bank details are changed.");
      return;
    }

    try {
      setIsLoading(true);
      const endpoint = isEditMode ? `${BASE_URL}/physicians/${physicianId}` : `${BASE_URL}/physicians`;
      const method = isEditMode ? "put" : "post";
      const resp = await axios[method](endpoint, formData, { headers: getAuthHeaders() });
      toast.success(isEditMode ? "Physician updated successfully." : "Physician added successfully.");
      // setTimeout(() => navigate("/physician"), 5000);
    } catch (err) {
      let errorMessage = isEditMode ? "Failed to update physician." : "Failed to add physician.";
      const responseMessage = err?.response?.data?.message;
      const apiValidationErrors =
        responseMessage && typeof responseMessage === "object" ? responseMessage : {};
      if (err?.response?.data?.message) {
        if (typeof err.response.data.message === "object") {
          Object.entries(err.response.data.message).forEach(([key, messages]) => {
            const messageArray = Array.isArray(messages) ? messages : [messages];
            const match = key.match(/^provider_affiliations\.(\d+)\.(.+)$/);
            if (match) {
              const index = parseInt(match[1]);
              const provider = providers.find(p => p.value === formData.provider_affiliations[index]?.provider_code);
              const providerName = provider ? provider.label : "Unknown Provider";
              messageArray.forEach(message => {
                toast.error(`${providerName}: ${message}`);
              });
            } else {
              messageArray.forEach(message => {
                toast.error(message);
              });
            }
          });
        } else {
          errorMessage = err.response.data.message;
          toast.error(errorMessage);
        }
      } else {
        toast.error(errorMessage);
      }
      console.error("Error submitting form:", errorMessage);
      setErrors(apiValidationErrors);
      if (apiValidationErrors.email_add) {
        setActiveTab("contact");
      } else if (apiValidationErrors.phic_from || apiValidationErrors.phic_to) {
        setActiveTab("license");
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 py-4 px-4 sm:px-3 lg:px-4">
      <ToastContainer position="top-right" />
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="max-w-7xl mx-auto">
        <div className="bg-white shadow-md rounded-lg overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex justify-between items-center">
              <h1 className="text-xl font-semibold text-gray-900">{isEditMode ? "Edit Physician" : "Add New Physician"}</h1>
              <div className="flex space-x-2">
                <button
                  onClick={() => navigate(-1)}
                  className="flex items-center space-x-1 text-gray-600 hover:text-gray-800 text-xs"
                >
                  <ArrowLeftIcon className="h-5 w-5" />
                  <span>Back</span>
                </button>
              </div>
            </div>
          </div>
          <div className="px-6 py-4">
            <div className="border-b border-gray-200 mb-4">
              <nav className="-mb-px flex space-x-8">
                {[
                  "information",
                  "contact",
                  "bank",
                  "address",
                  "license",
                  ...(isEditMode ? ["maintenance"] : []),
                  "provider_affiliates",
                ].map(tab => (
                  <button
                    key={tab}
                    onClick={() => setActiveTab(tab)}
                    className={`${
                      activeTab === tab
                        ? "border-blue-500 text-blue-600"
                        : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                    } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm capitalize`}
                  >
                    {tab.replace("_", " ")}
                  </button>
                ))}
              </nav>
            </div>
            <form onSubmit={handleSubmit}>
              {isLoading ? (
                <div className="flex justify-center py-4">
                  <svg
                    className="animate-spin h-6 w-6 text-blue-600"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                </div>
              ) : (
                <>
                  {activeTab === "information" && (
                    <div className={tabSectionClass}>
                      <div>
                        <h2 className="text-lg font-semibold text-gray-900 mb-4">Personal Information</h2>
                        <div className={fieldGridClass}>
                          {isEditMode && (
                            <div>
                              <label className="block text-xs font-medium text-gray-700">Physician ID</label>
                              <p className="mt-1 text-xs text-gray-900">{formData.physician_id || "N/A"}</p>
                            </div>
                          )}
                          <div>
                            <label className="block text-xs font-medium text-gray-700">Last Name <span className="text-red-500">*</span></label>
                            <input
                              type="text"
                              value={formData.last_name}
                              onChange={(e) => handleInputChange(e, "last_name")}
                              className="mt-1 block w-full border border-gray-200 rounded-md px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-500 text-xs"
                              placeholder="Enter last name"
                              disabled={isLoading}
                              required
                            />
                            {errors.last_name && <p className="mt-1 text-xs text-red-500">{errors.last_name[0]}</p>}
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-700">First Name <span className="text-red-500">*</span></label>
                            <input
                              type="text"
                              value={formData.first_name}
                              onChange={(e) => handleInputChange(e, "first_name")}
                              className="mt-1 block w-full border border-gray-200 rounded-md px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-500 text-xs"
                              placeholder="Enter first name"
                              disabled={isLoading}
                              required
                            />
                            {errors.first_name && <p className="mt-1 text-xs text-red-500">{errors.first_name[0]}</p>}
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-700">Middle Name</label>
                            <input
                              type="text"
                              value={formData.middle_name}
                              onChange={(e) => handleInputChange(e, "middle_name")}
                              className="mt-1 block w-full border border-gray-200 rounded-md px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-500 text-xs"
                              placeholder="Enter middle name"
                              disabled={isLoading}
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-700">Extension</label>
                            <input
                              type="text"
                              value={formData.extension}
                              onChange={(e) => handleInputChange(e, "extension")}
                              className="mt-1 block w-full border border-gray-200 rounded-md px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-500 text-xs"
                              placeholder="e.g., Jr., Sr."
                              disabled={isLoading}
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-700">Mother's Name</label>
                            <input
                              type="text"
                              value={formData.mothers_name}
                              onChange={(e) => handleInputChange(e, "mothers_name")}
                              className="mt-1 block w-full border border-gray-200 rounded-md px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-500 text-xs"
                              placeholder="Enter mother's name"
                              disabled={isLoading}
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-700">Nationality</label>
                            <Select
                              options={nationalities}
                              value={nationalities.find(n => n.value === formData.nationality) || null}
                              onChange={(selected) => handleSelectChange(selected, "nationality")}
                              placeholder="Select nationality"
                              isClearable
                              className="mt-1 text-xs"
                              classNamePrefix="select"
                              isDisabled={isLoading}
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-700">Date of Birth</label>
                            <input
                              type="date"
                              value={formData.date_of_birth}
                              onChange={(e) => handleInputChange(e, "date_of_birth")}
                              className="mt-1 block w-full border border-gray-200 rounded-md px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-500 text-xs"
                              disabled={isLoading}
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-700">Age</label>
                            <input
                              type="number"
                              value={formData.age}
                              onChange={(e) => handleInputChange(e, "age")}
                              className="mt-1 block w-full border border-gray-200 rounded-md px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-500 text-xs"
                              placeholder="Enter age"
                              disabled={isLoading}
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-700">Civil Status</label>
                            <select
                              value={formData.civil_status}
                              onChange={(e) => handleInputChange(e, "civil_status")}
                              className="mt-1 block w-full border border-gray-200 rounded-md px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-500 text-xs"
                              data-placeholder="Select a status"
                              placeholder="Select a status"
                              disabled={isLoading}
                            >
                              <option value="Single">Single</option>
                              <option value="Divorced">Divorced</option>
                              <option value="Separated">Separated</option>
                              <option value="Widowed">Widowed</option>
                              <option value="Living common law">Living common law</option>
                              <option value="Married">Married</option>
                            </select>
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-700">Gender</label>
                            <select
                              value={formData.gender}
                              onChange={(e) => handleInputChange(e, "gender")}
                              className="mt-1 block w-full border border-gray-200 rounded-md px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-500 text-xs"
                              disabled={isLoading}
                            >
                              <option value="">Select gender</option>
                              <option value="m">Male</option>
                              <option value="f">Female</option>
                            </select>
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-700">Specialization <span className="text-red-500">*</span></label>
                            <Select
                              options={specializations}
                              value={selectedSpecializationOption}
                              onChange={(selected) => handleSelectChange(selected, "specialization")}
                              placeholder="Select specialization"
                              isClearable
                              className="mt-1 text-xs"
                              classNamePrefix="select"
                              isDisabled={isLoading}
                              required
                            />
                            {errors.specialization && <p className="mt-1 text-xs text-red-500">{errors.specialization[0]}</p>}
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-700">Sub Specialization</label>
                            <Select
                              options={filteredSubSpecializationOptions}
                              value={selectedSubSpecializationOption}
                              onChange={(selected) => handleSelectChange(selected, "sub_specialization")}
                              placeholder="Select sub specialization"
                              isClearable
                              className="mt-1 text-xs"
                              classNamePrefix="select"
                              isDisabled={isLoading}
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-700">IP Prescription Period</label>
                            <input
                              type="number"
                              value={formData.ip_prescription_period}
                              onChange={(e) => handleInputChange(e, "ip_prescription_period")}
                              className="mt-1 block w-full border border-gray-200 rounded-md px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-500 text-xs"
                              placeholder="Enter IP prescription period"
                              disabled={isLoading}
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-700">OP Prescription Period</label>
                            <input
                              type="number"
                              value={formData.op_prescription_period}
                              onChange={(e) => handleInputChange(e, "op_prescription_period")}
                              className="mt-1 block w-full border border-gray-200 rounded-md px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-500 text-xs"
                              placeholder="Enter OP prescription period"
                              disabled={isLoading}
                            />
                          </div>
                          <div className={wideFieldClass}>
                            <label className="block text-xs font-medium text-gray-700">Remarks</label>
                            <textarea
                              value={formData.remarks}
                              onChange={(e) => handleInputChange(e, "remarks")}
                              className="mt-1 block w-full border border-gray-200 rounded-md px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-500 text-xs"
                              placeholder="Enter remarks"
                              disabled={isLoading}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                  {activeTab === "contact" && (
                    <div className={tabSectionClass}>
                      <div>
                        <h2 className="text-lg font-semibold text-gray-900 mb-4">Contact Information</h2>
                        <div className={fieldGridClass}>
                          <div>
                            <label className="block text-xs font-medium text-gray-700">Telephone Number</label>
                            <input
                              type="text"
                              value={formData.tel_no}
                              onChange={(e) => handleInputChange(e, "tel_no")}
                              className="mt-1 block w-full border border-gray-200 rounded-md px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-500 text-xs"
                              placeholder="Enter telephone number"
                              disabled={isLoading}
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-700">Mobile Number</label>
                            <input
                              type="text"
                              value={formData.mobile_no}
                              onChange={(e) => handleInputChange(e, "mobile_no")}
                              className="mt-1 block w-full border border-gray-200 rounded-md px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-500 text-xs"
                              placeholder="Enter mobile number"
                              disabled={isLoading}
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-700">Email Address</label>
                            <input
                              type="email"
                              value={formData.email_add}
                              onChange={(e) => handleInputChange(e, "email_add")}
                              className="mt-1 block w-full border border-gray-200 rounded-md px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-500 text-xs"
                              placeholder="Enter email address"
                              disabled={isLoading}
                            />
                            {getFieldErrorMessage("email_add") && (
                              <p className="mt-1 text-xs text-red-500">
                                {getFieldErrorMessage("email_add")}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                  {activeTab === "bank" && (
                    <div className={tabSectionClass}>
                      <div>
                        <h2 className="text-lg font-semibold text-gray-900 mb-4">Bank Details</h2>
                        <div className={fieldGridClass}>
                          <div>
                            <label className="block text-xs font-medium text-gray-700">Bank Account Name</label>
                            <input
                              type="text"
                              value={formData.bank_acc_name}
                              onChange={(e) => handleInputChange(e, "bank_acc_name")}
                              className="mt-1 block w-full border border-gray-200 rounded-md px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-500 text-xs"
                              placeholder="Enter bank account name"
                              disabled={isLoading}
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-700">Bank Account Number</label>
                            <input
                              type="text"
                              value={formData.bank_acct_no}
                              onChange={(e) => handleInputChange(e, "bank_acct_no")}
                              className="mt-1 block w-full border border-gray-200 rounded-md px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-500 text-xs"
                              placeholder="Enter bank account number"
                              disabled={isLoading}
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-700">Bank</label>
                            <Select
                              options={banks}
                              value={banks.find(b => b.value === formData.bank_id) || null}
                              onChange={(selected) => handleSelectChange(selected, "bank_id")}
                              placeholder="Select bank"
                              isClearable
                              className="mt-1 text-xs"
                              classNamePrefix="select"
                              isDisabled={isLoading}
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-700">Bank Branch</label>
                            <input
                              type="text"
                              value={formData.bank_branch}
                              onChange={(e) => handleInputChange(e, "bank_branch")}
                              className="mt-1 block w-full border border-gray-200 rounded-md px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-500 text-xs"
                              placeholder="Enter bank branch"
                              disabled={isLoading}
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-700">Suspected Fraudulent Physician</label>
                            <input
                              type="checkbox"
                              checked={formData.fraud_flag}
                              onChange={(e) => handleInputChange(e, "fraud_flag")}
                              className="mt-1 h-5 w-5 text-blue-600 border-gray-200 rounded focus:ring-blue-500"
                              disabled={isLoading}
                            />
                          </div>
                          <div className="md:col-span-2">
                            <label className="block text-xs font-medium text-gray-700">Authorization Letter for Settlement</label>
                            <input
                              type="file"
                              onChange={(e) => handleFileUpload(e, "pef")}
                              className="mt-1 block w-full border border-gray-200 rounded-md px-2 py-1.5 text-xs"
                              accept=".png,.jpeg,.jpg,.bmp,.pdf,.doc,.docx,.xls,.xlsx,.csv"
                              disabled={isLoading}
                            />
                            {formData.pef_file_name && (
                              <div className="mt-1 flex items-center space-x-2">
                                <a
                                  href={formData.pef_signed_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-blue-600 hover:underline text-xs"
                                >
                                  {formData.pef_file_name}
                                </a>
                                {isFileUrlExpired(formData.pef_expires_at) && (
                                  <button
                                    onClick={() => regenerateUrl("pef")}
                                    className="flex items-center space-x-1 text-gray-600 hover:text-gray-800 text-xs"
                                  >
                                    <ArrowPathIcon className="h-5 w-5" />
                                    <span>Regenerate URL</span>
                                  </button>
                                )}
                              </div>
                            )}
                          </div>
                          <div className="md:col-span-2">
                            <label className="block text-xs font-medium text-gray-700">
                              Bank Proof
                              {physicianBankChanged() && (
                                <span className="text-red-600 ml-1">*</span>
                              )}
                            </label>
                            <input
                              type="file"
                              onChange={handleBankProofUpload}
                              className="mt-1 block w-full border border-gray-200 rounded-md px-2 py-1.5 text-xs"
                              accept=".png,.jpeg,.jpg,.bmp,.pdf,.doc,.docx,.xls,.xlsx,.csv"
                              disabled={isLoading}
                            />
                            {formData.bank_proof_file_name && (
                              <div className="mt-1 flex items-center space-x-2">
                                <a
                                  href={formData.bank_proof_signed_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-blue-600 hover:underline text-xs"
                                >
                                  {formData.bank_proof_file_name}
                                </a>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                  {activeTab === "address" && (
                    <div className="space-y-6">
                      <div>
                        <h2 className="text-lg font-semibold text-gray-900 mb-4">Home Address</h2>
                        <div className={fieldGridClass}>
                          <div className={wideFieldClass}>
                            <label className="block text-xs font-medium text-gray-700">Street/Building/Subdivision/Village</label>
                            <input
                              type="text"
                              value={formData.home_streetno_bldg}
                              onChange={(e) => handleInputChange(e, "home_streetno_bldg")}
                              className="mt-1 block w-full border border-gray-200 rounded-md px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-500 text-xs"
                              placeholder="Enter street/building/subdivision/village"
                              disabled={isLoading}
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-700">Region</label>
                            <Select
                              options={homeRegions}
                              value={homeRegions.find(r => r.value === formData.home_region) || null}
                              onChange={handleHomeRegionChange}
                              placeholder="Select region"
                              isClearable
                              className="mt-1 text-xs"
                              classNamePrefix="select"
                              isDisabled={isLoading || isLoadingHomeAddress.region}
                              isLoading={isLoadingHomeAddress.region}
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-700">Province</label>
                            <Select
                              options={homeProvinces}
                              value={homeProvinces.find(p => p.value === formData.home_province) || null}
                              onChange={handleHomeProvinceChange}
                              placeholder="Select province"
                              isClearable
                              className="mt-1 text-xs"
                              classNamePrefix="select"
                              isDisabled={isLoading || isLoadingHomeAddress.province || !formData.home_region}
                              isLoading={isLoadingHomeAddress.province}
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-700">Town/City</label>
                            <Select
                              options={homeTownCities}
                              value={homeTownCities.find(t => t.value === formData.home_towncity) || null}
                              onChange={handleHomeTownCityChange}
                              placeholder="Select town/city"
                              isClearable
                              className="mt-1 text-xs"
                              classNamePrefix="select"
                              isDisabled={isLoading || isLoadingHomeAddress.towncity || !formData.home_province}
                              isLoading={isLoadingHomeAddress.towncity}
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-700">Barangay</label>
                            <Select
                              options={homeBarangays}
                              value={homeBarangays.find(b => b.value === formData.home_barangay) || null}
                              onChange={handleHomeBarangayChange}
                              placeholder="Select barangay"
                              isClearable
                              className="mt-1 text-xs"
                              classNamePrefix="select"
                              isDisabled={isLoading || isLoadingHomeAddress.barangay || !formData.home_towncity}
                              isLoading={isLoadingHomeAddress.barangay}
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-700">Postal Code</label>
                            <input
                              type="text"
                              value={formData.home_postalcode}
                              className="mt-1 block w-full border border-gray-200 rounded-md px-2 py-1.5 bg-gray-100 cursor-not-allowed text-xs"
                              disabled
                            />
                          </div>
                        </div>
                      </div>
                      <div>
                        <h2 className="text-lg font-semibold text-gray-900 mb-4">Provincial Address</h2>
                        <div className={fieldGridClass}>
                          <div className={wideFieldClass}>
                            <label className="block text-xs font-medium text-gray-700">Street/Building/Subdivision/Village</label>
                            <input
                              type="text"
                              value={formData.provincial_streetno_bldg}
                              onChange={(e) => handleInputChange(e, "provincial_streetno_bldg")}
                              className="mt-1 block w-full border border-gray-200 rounded-md px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-500 text-xs"
                              placeholder="Enter street/building/subdivision/village"
                              disabled={isLoading}
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-700">Region</label>
                            <Select
                              options={provincialRegions}
                              value={provincialRegions.find(r => r.value === formData.provincial_region) || null}
                              onChange={handleProvincialRegionChange}
                              placeholder="Select region"
                              isClearable
                              className="mt-1 text-xs"
                              classNamePrefix="select"
                              isDisabled={isLoading || isLoadingProvincialAddress.region}
                              isLoading={isLoadingProvincialAddress.region}
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-700">Province</label>
                            <Select
                              options={provincialProvinces}
                              value={provincialProvinces.find(p => p.value === formData.provincial_province) || null}
                              onChange={handleProvincialProvinceChange}
                              placeholder="Select province"
                              isClearable
                              className="mt-1 text-xs"
                              classNamePrefix="select"
                              isDisabled={isLoading || isLoadingProvincialAddress.province || !formData.provincial_region}
                              isLoading={isLoadingProvincialAddress.province}
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-700">Town/City</label>
                            <Select
                              options={provincialTownCities}
                              value={provincialTownCities.find(t => t.value === formData.provincial_towncity) || null}
                              onChange={handleProvincialTownCityChange}
                              placeholder="Select town/city"
                              isClearable
                              className="mt-1 text-xs"
                              classNamePrefix="select"
                              isDisabled={isLoading || isLoadingProvincialAddress.towncity || !formData.provincial_province}
                              isLoading={isLoadingProvincialAddress.towncity}
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-700">Barangay</label>
                            <Select
                              options={provincialBarangays}
                              value={provincialBarangays.find(b => b.value === formData.provincial_barangay) || null}
                              onChange={handleProvincialBarangayChange}
                              placeholder="Select barangay"
                              isClearable
                              className="mt-1 text-xs"
                              classNamePrefix="select"
                              isDisabled={isLoading || isLoadingProvincialAddress.barangay || !formData.provincial_towncity}
                              isLoading={isLoadingProvincialAddress.barangay}
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-700">Postal Code</label>
                            <input
                              type="text"
                              value={formData.provincial_postalcode}
                              className="mt-1 block w-full border border-gray-200 rounded-md px-2 py-1.5 bg-gray-100 cursor-not-allowed text-xs"
                              disabled
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                  {activeTab === "license" && (
                    <div className={tabSectionClass}>
                      <div>
                        <h2 className="text-lg font-semibold text-gray-900 mb-4">License Details</h2>
                        <div className={fieldGridClass}>
                          <div>
                            <label className="block text-xs font-medium text-gray-700">TIN Number <span className="text-red-500">*</span></label>
                            <input
                              type="text"
                              value={formData.tin_no}
                              onChange={(e) => handleInputChange(e, "tin_no")}
                              className="mt-1 block w-full border border-gray-200 rounded-md px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-500 text-xs"
                              placeholder="Enter TIN number"
                              disabled={isLoading}
                              required
                            />
                            {errors.tin_no && <p className="mt-1 text-xs text-red-500">{errors.tin_no[0]}</p>}
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-700">SSS/GSIS Number</label>
                            <input
                              type="text"
                              value={formData.sss_no}
                              onChange={(e) => handleInputChange(e, "sss_no")}
                              className="mt-1 block w-full border border-gray-200 rounded-md px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-500 text-xs"
                              placeholder="Enter SSS/GSIS number"
                              disabled={isLoading}
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-700">Philhealth Number</label>
                            <input
                              type="text"
                              value={formData.philhealth_no}
                              onChange={(e) => handleInputChange(e, "philhealth_no")}
                              className="mt-1 block w-full border border-gray-200 rounded-md px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-500 text-xs"
                              placeholder="Enter Philhealth number"
                              disabled={isLoading}
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-700">PHIC Number</label>
                            <input
                              type="text"
                              value={formData.phic_no}
                              onChange={(e) => handleInputChange(e, "phic_no")}
                              className="mt-1 block w-full border border-gray-200 rounded-md px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-500 text-xs"
                              placeholder="Enter PHIC number"
                              disabled={isLoading}
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-700">PHIC Date From</label>
                            <input
                              type="date"
                              value={formData.phic_from}
                              onChange={(e) => handleInputChange(e, "phic_from")}
                              className="mt-1 block w-full border border-gray-200 rounded-md px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-500 text-xs"
                              disabled={isLoading}
                            />
                            {getFieldErrorMessage("phic_from") && (
                              <p className="mt-1 text-xs text-red-500">
                                {getFieldErrorMessage("phic_from")}
                              </p>
                            )}
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-700">PHIC Date To</label>
                            <input
                              type="date"
                              value={formData.phic_to}
                              onChange={(e) => handleInputChange(e, "phic_to")}
                              className="mt-1 block w-full border border-gray-200 rounded-md px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-500 text-xs"
                              disabled={isLoading}
                            />
                            {getFieldErrorMessage("phic_to") && (
                              <p className="mt-1 text-xs text-red-500">
                                {getFieldErrorMessage("phic_to")}
                              </p>
                            )}
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-700">PRC License Number <span className="text-red-500">*</span></label>
                            <input
                              type="text"
                              value={formData.prc_no}
                              onChange={(e) => handleInputChange(e, "prc_no")}
                              className="mt-1 block w-full border border-gray-200 rounded-md px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-500 text-xs"
                              placeholder="Enter PRC license number"
                              disabled={isLoading}
                              required
                            />
                            {errors.prc_no && <p className="mt-1 text-xs text-red-500">{errors.prc_no[0]}</p>}
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-700">PRC Validity Date</label>
                            <input
                              type="date"
                              value={formData.prc_expiry}
                              onChange={(e) => handleInputChange(e, "prc_expiry")}
                              className="mt-1 block w-full border border-gray-200 rounded-md px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-500 text-xs"
                              disabled={isLoading}
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-700">Tax Percentage</label>
                            <input
                              type="number"
                              value={formData.tax_percentage}
                              onChange={(e) => handleInputChange(e, "tax_percentage")}
                              className="mt-1 block w-full border border-gray-200 rounded-md px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-500 text-xs"
                              placeholder="Enter tax percentage"
                              disabled={isLoading}
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-700">Vatable</label>
                            <select
                              value={String(formData.vatable ?? "0")}
                              onChange={(e) => handleInputChange(e, "vatable")}
                              className="mt-1 block w-full border border-gray-200 rounded-md px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-500 text-xs"
                              disabled={isLoading}
                            >
                              <option value="0">False</option>
                              <option value="1">True</option>
                            </select>
                            {errors.vatable && <p className="mt-1 text-xs text-red-500">{errors.vatable[0]}</p>}
                          </div>
                          <div className="md:col-span-2">
                            <label className="block text-xs font-medium text-gray-700">Sworn Attachment</label>
                            <input
                              type="file"
                              onChange={(e) => handleFileUpload(e, "sworn_attachment")}
                              className="mt-1 block w-full border border-gray-200 rounded-md px-2 py-1.5 text-xs"
                              accept=".png,.jpeg,.jpg,.bmp,.pdf,.doc,.docx,.xls,.xlsx,.csv"
                              disabled={isLoading}
                            />
                            {formData.sworn_attachment_file_name && (
                              <div className="mt-1 flex items-center space-x-2">
                                <a
                                  href={formData.sworn_attachment_signed_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-blue-600 hover:underline text-xs"
                                >
                                  {formData.sworn_attachment_file_name}
                                </a>
                                {isFileUrlExpired(formData.sworn_attachment_expires_at) && (
                                  <button
                                    onClick={() => regenerateUrl("sworn_attachment")}
                                    className="flex items-center space-x-1 text-gray-600 hover:text-gray-800 text-xs"
                                  >
                                    <ArrowPathIcon className="h-5 w-5" />
                                    <span>Regenerate URL</span>
                                  </button>
                                )}
                              </div>
                            )}
                          </div>
                          <div className="md:col-span-2">
                            <label className="block text-xs font-medium text-gray-700">Conforme File</label>
                            <input
                              type="file"
                              onChange={(e) => handleFileUpload(e, "conforme_file")}
                              className="mt-1 block w-full border border-gray-200 rounded-md px-2 py-1.5 text-xs"
                              accept=".png,.jpeg,.jpg,.bmp,.pdf,.doc,.docx,.xls,.xlsx,.csv"
                              disabled={isLoading}
                            />
                            {formData.conforme_file_file_name && (
                              <div className="mt-1 flex items-center space-x-2">
                                <a
                                  href={formData.conforme_file_signed_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-blue-600 hover:underline text-xs"
                                >
                                  {formData.conforme_file_file_name}
                                </a>
                                {isFileUrlExpired(formData.conforme_file_expires_at) && (
                                  <button
                                    onClick={() => regenerateUrl("conforme_file")}
                                    className="flex items-center space-x-1 text-gray-600 hover:text-gray-800 text-xs"
                                  >
                                    <ArrowPathIcon className="h-5 w-5" />
                                    <span>Regenerate URL</span>
                                  </button>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                  {activeTab === "maintenance" && (
                    <div>
                      <h2 className="text-lg font-semibold text-gray-900 mb-4">Maintenance</h2>
                      <div className={fieldGridClass}>
                        <div>
                          <label className="block text-xs font-medium text-gray-700">
                            Update For <span className="text-red-500">*</span>
                          </label>
                          <select
                            value={formData.maintenance_update_for}
                            onChange={(e) => handleInputChange(e, "maintenance_update_for")}
                            className={`mt-1 block w-full border ${
                              getFieldErrorMessage("maintenance_update_for")
                                ? "border-red-500"
                                : "border-gray-200"
                            } rounded-md px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-500 text-xs`}
                            disabled={isLoading}
                          >
                            <option value="">Select update for</option>
                            <option value="suspension">Suspension</option>
                            <option value="reactivation">Reactivation</option>
                            <option value="termination">Termination</option>
                            <option value="extension">Extension</option>
                          </select>
                          {getFieldErrorMessage("maintenance_update_for") && (
                            <p className="mt-1 text-xs text-red-500">
                              {getFieldErrorMessage("maintenance_update_for")}
                            </p>
                          )}
                        </div>

                        <div>
                          <label className="block text-xs font-medium text-gray-700">
                            Effectivity Date <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="date"
                            value={formData.maintenance_effectivity_date}
                            onChange={(e) => handleInputChange(e, "maintenance_effectivity_date")}
                            className={`mt-1 block w-full border ${
                              getFieldErrorMessage("maintenance_effectivity_date")
                                ? "border-red-500"
                                : "border-gray-200"
                            } rounded-md px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-500 text-xs`}
                            disabled={isLoading}
                          />
                          {getFieldErrorMessage("maintenance_effectivity_date") && (
                            <p className="mt-1 text-xs text-red-500">
                              {getFieldErrorMessage("maintenance_effectivity_date")}
                            </p>
                          )}
                        </div>

                        <div className={wideFieldClass}>
                          <label className="block text-xs font-medium text-gray-700">
                            Reason <span className="text-red-500">*</span>
                          </label>
                          <textarea
                            value={formData.maintenance_reason}
                            onChange={(e) => handleInputChange(e, "maintenance_reason")}
                            className={`mt-1 block w-full border ${
                              getFieldErrorMessage("maintenance_reason")
                                ? "border-red-500"
                                : "border-gray-200"
                            } rounded-md px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-500 text-xs`}
                            rows={3}
                            placeholder="Enter reason"
                            disabled={isLoading}
                          />
                          {getFieldErrorMessage("maintenance_reason") && (
                            <p className="mt-1 text-xs text-red-500">
                              {getFieldErrorMessage("maintenance_reason")}
                            </p>
                          )}
                        </div>

                        <div className={wideFieldClass}>
                          <label className="block text-xs font-medium text-gray-700">Remarks</label>
                          <textarea
                            value={formData.maintenance_remarks}
                            onChange={(e) => handleInputChange(e, "maintenance_remarks")}
                            className={`mt-1 block w-full border ${
                              getFieldErrorMessage("maintenance_remarks")
                                ? "border-red-500"
                                : "border-gray-200"
                            } rounded-md px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-500 text-xs`}
                            rows={3}
                            placeholder="Enter remarks"
                            disabled={isLoading}
                          />
                          {getFieldErrorMessage("maintenance_remarks") && (
                            <p className="mt-1 text-xs text-red-500">
                              {getFieldErrorMessage("maintenance_remarks")}
                            </p>
                          )}
                        </div>
                      </div>

                      {(formData.maintenance_lastupdate_date || formData.maintenance_update_by) && (
                        <div className="mt-4 p-3 bg-gray-50 border border-gray-200 rounded-md text-xs text-gray-700">
                          <p>
                            <span className="font-medium">Last Updated:</span>{" "}
                            {formData.maintenance_lastupdate_date || "N/A"}
                          </p>
                          <p>
                            <span className="font-medium">Updated By:</span>{" "}
                            {formData.maintenance_update_by || "N/A"}
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                  {activeTab === "provider_affiliates" && (
                    <div>
                      <h2 className="text-lg font-semibold text-gray-900 mb-4">Provider Affiliations</h2>
                      <div className="space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4 mb-4">
                          <div>
                            <label className="block text-xs font-medium text-gray-700">Provider <span className="text-red-500">*</span></label>
                            <Select
                              options={providers.filter(
                                provider => !formData.provider_affiliations.some(aff => aff.provider_code === provider.value)
                              )}
                              value={providers.find(option => option.value === providerForm.provider_code) || null}
                              onChange={handleProviderSelect}
                              onInputChange={(inputValue, { action }) => {
                                if (action === "input-change") {
                                  setProviderSearch(inputValue);
                                }
                                return inputValue;
                              }}
                              placeholder="Select or search provider"
                              isClearable
                              isLoading={isLoadingProviders}
                              loadingMessage={() => "Searching providers..."}
                              noOptionsMessage={({ inputValue }) =>
                                inputValue ? "No matching active provider found" : "Type to search provider"
                              }
                              className="mt-1 text-xs"
                              classNamePrefix="select"
                              isDisabled={isLoading}
                            />
                            {errors.provider_code && (
                              <p className="mt-1 text-xs text-red-500">{errors.provider_code}</p>
                            )}
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-700">Room Number</label>
                            <input
                              type="text"
                              name="room_no"
                              value={providerForm.room_no}
                              onChange={handleProviderFormChange}
                              className="mt-1 block w-full border border-gray-200 rounded-md px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-500 text-xs"
                              placeholder="Enter room number"
                              disabled={isLoading}
                              maxLength="200"
                            />
                            {errors.room_no && (
                              <p className="mt-1 text-xs text-red-500">{errors.room_no}</p>
                            )}
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-700">Schedule</label>
                            <input
                              type="text"
                              name="schedule"
                              value={providerForm.schedule}
                              onChange={handleProviderFormChange}
                              className="mt-1 block w-full border border-gray-200 rounded-md px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-500 text-xs"
                              placeholder="Enter schedule"
                              disabled={isLoading}
                              maxLength="200"
                            />
                            {errors.schedule && (
                              <p className="mt-1 text-xs text-red-500">{errors.schedule}</p>
                            )}
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-700">Remarks</label>
                            <input
                              type="text"
                              name="remarks"
                              value={providerForm.remarks}
                              onChange={handleProviderFormChange}
                              className="mt-1 block w-full border border-gray-200 rounded-md px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-500 text-xs"
                              placeholder="Enter remarks"
                              disabled={isLoading}
                              maxLength="200"
                            />
                            {errors.remarks && (
                              <p className="mt-1 text-xs text-red-500">{errors.remarks}</p>
                            )}
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={addProviderAffiliation}
                          className="flex items-center space-x-1 bg-blue-600 text-white px-3 py-1.5 rounded-md hover:bg-blue-700 transition-colors text-xs"
                          disabled={isLoading}
                        >
                          <PlusIcon className="h-5 w-5" />
                          <span>Add Provider</span>
                        </button>
                        <div className="flex justify-between items-center mb-4">
                          <div className="flex items-center space-x-2 relative">
                            <label className="text-xs font-medium text-gray-700">Search:</label>
                            <input
                              type="text"
                              value={searchQuery}
                              onChange={(e) => setSearchQuery(e.target.value)}
                              className="pl-8 pr-4 py-1.5 border border-gray-200 rounded-md text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
                              placeholder="Search by provider code or name"
                            />
                            <MagnifyingGlassIcon className="h-5 w-5 text-gray-400 absolute left-10 top-1/2 transform -translate-y-1/2" />
                          </div>
                          <div className="flex items-center space-x-2">
                            <label className="text-xs font-medium text-gray-700">Show entries:</label>
                            <select
                              value={itemsPerPage}
                              onChange={handleItemsPerPageChange}
                              className="border border-gray-200 rounded-md px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
                            >
                              <option value={10}>10</option>
                              <option value={25}>25</option>
                              <option value={50}>50</option>
                              <option value={100}>100</option>
                            </select>
                          </div>
                        </div>
                        <div className="overflow-x-auto">
                          <table className="min-w-full border border-gray-200">
                            <thead className="bg-gray-50">
                              <tr>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Provider Code</th>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Provider Name</th>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Room Number</th>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Schedule</th>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Remarks</th>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                              </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                              {paginatedAffiliations.map((affiliation, index) => {
                                const provider = providers.find(p => p.value === affiliation.provider_code);
                                return (
                                  <tr key={index}>
                                    <td className="px-4 py-2 text-xs text-gray-900">{affiliation.provider_code}</td>
                                    {/* <td className="px-4 py-2 text-xs text-gray-900">{affiliation.provider_name}</td> */}
                                    {/* <td className="px-4 py-2 text-xs text-gray-900">{provider ? provider.label : "N/A"}</td> */}
                                    <td className="px-4 py-2 text-xs text-gray-900">
                                      {affiliation?.provider_name || provider?.name || provider?.label || "N/A"}
                                    </td>
                                    <td className="px-4 py-2 text-xs text-gray-900">{affiliation.room_no || "N/A"}</td>
                                    <td className="px-4 py-2 text-xs text-gray-900">{affiliation.schedule || "N/A"}</td>
                                    <td className="px-4 py-2 text-xs text-gray-900">{affiliation.remarks || "N/A"}</td>
                                    <td className="px-4 py-2 text-xs">
                                      <button
                                        type="button"
                                        onClick={() => removeProviderAffiliation((currentPage - 1) * itemsPerPage + index)}
                                        className="flex items-center space-x-1 text-red-600 hover:text-red-800 text-xs"
                                        disabled={isLoading}
                                      >
                                        <TrashIcon className="h-5 w-5" />
                                        <span>Remove</span>
                                      </button>
                                    </td>
                                  </tr>
                                );
                              })}
                              {paginatedAffiliations.length === 0 && (
                                <tr>
                                  <td colSpan="6" className="px-4 py-2 text-xs text-gray-500 text-center">
                                    {searchQuery ? "No matching provider affiliations found." : "No provider affiliations added."}
                                  </td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        </div>
                        <div className="flex justify-between items-center mt-4">
                          <div className="text-xs text-gray-600">
                            Showing {totalItems > 0 ? (currentPage - 1) * itemsPerPage + 1 : 0} to {Math.min(currentPage * itemsPerPage, totalItems)} of {totalItems} entries
                          </div>
                          <div className="flex space-x-2">
                            <button
                              onClick={() => handlePageChange(currentPage - 1)}
                              disabled={currentPage === 1 || isLoading}
                              className="px-3 py-1.5 border border-gray-200 rounded-md text-xs text-gray-600 hover:bg-gray-100 disabled:opacity-50"
                            >
                              Previous
                            </button>
                            <span className="px-3 py-1.5 text-xs text-gray-600">
                              Page {currentPage} of {totalPages}
                            </span>
                            <button
                              onClick={() => handlePageChange(currentPage + 1)}
                              disabled={currentPage === totalPages || isLoading}
                              className="px-3 py-1.5 border border-gray-200 rounded-md text-xs text-gray-600 hover:bg-gray-100 disabled:opacity-50"
                            >
                              Next
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                  <div className="mt-4 flex justify-end">
                    <button
                      type="submit"
                      className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 transition-colors text-xs flex items-center space-x-2"
                      disabled={isLoading}
                    >
                      {isLoading ? (
                        <>
                          <svg
                            className="animate-spin h-5 w-5 text-white"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                          >
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                            ></circle>
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            ></path>
                          </svg>
                          <span>
                            {activeTab === "maintenance"
                              ? "Saving Maintenance..."
                              : isEditMode
                              ? "Updating..."
                              : "Saving..."}
                          </span>
                        </>
                      ) : (
                        <>
                          <DocumentIcon className="h-5 w-5" />
                          <span>
                            {activeTab === "maintenance"
                              ? "Save Maintenance"
                              : isEditMode
                              ? "Update"
                              : "Save"}
                          </span>
                        </>
                      )}
                    </button>
                  </div>
                </>
              )}
            </form>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default PhysicianEnrollmentForm;
